import { defineStore } from "pinia";
import { ref } from "vue";
import * as graphql from "../graphql";
import apolloClient from "../graphql/client";
import { useServiceStore } from "./service";
import { useApolloErrorHandling } from "@/util/useApolloErrorHandling";

export interface EternalMods {
  game?: graphql.EternalGame;
  categories?: Array<graphql.EternalCategory>;
  featured?: graphql.EternalFeatured;
  installed?: Array<graphql.EternalMod>;
  searchResults?: graphql.EternalPaginatedMods;
}

export const useEternalStore = defineStore("eternal", () => {
  const service = useServiceStore();
  const { handleApolloErrorDefault } = useApolloErrorHandling();
  const eternalMods = ref<EternalMods>({
    game: undefined,
    categories: undefined,
    featured: undefined,
    installed: undefined,
    searchResults: undefined,
  });

  function appendEternalSearchResult(searchResults: graphql.EternalPaginatedMods) {
    if (eternalMods.value.searchResults) {
      eternalMods.value.searchResults.mods.push(...searchResults.mods);
      eternalMods.value.searchResults.pagination.index = searchResults.pagination.index;
      eternalMods.value.searchResults.pagination.resultCount += searchResults.pagination.resultCount;
    }
  }

  function setEternalSearchResult(searchResults: graphql.EternalPaginatedMods) {
    eternalMods.value.searchResults = searchResults;
  }

  function clearEternalSearchResult() {
    eternalMods.value.searchResults = undefined;
  }

  function setEternalBootstrap({
    game,
    categories,
    featured,
  }: {
    game: graphql.EternalGame;
    categories: Array<graphql.EternalCategory>;
    featured: graphql.EternalFeatured;
  }) {
    eternalMods.value.game = game;
    eternalMods.value.categories = categories;
    eternalMods.value.featured = featured;
  }

  function setEternalInstalledMods({ mods }: { mods: Array<graphql.EternalMod> }) {
    eternalMods.value.installed = mods;
  }

  async function eternalSearchMods({
    categoryId,
    searchQuery,
    sortBy,
    loadMore,
  }: {
    categoryId?: number;
    searchQuery?: string;
    sortBy?: graphql.EternalSortBy;
    loadMore: boolean;
  }) {
    const profile = service.ctx.profile?.name;
    if (!profile) return;
    const index = loadMore ? eternalMods.value.searchResults?.mods.length || 0 : 0;
    try {
      const result = await apolloClient.query({
        query: graphql.EternalSearchModsDocument,
        variables: {
          profile,
          categoryId,
          searchQuery,
          sortBy,
          index,
        },
      });
      if (result.data) {
        if (index > 0) {
          appendEternalSearchResult(result.data.eternalSearchMods);
        } else {
          setEternalSearchResult(result.data.eternalSearchMods);
        }
      }
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  async function loadEternalBootstrap({ profile }: { profile: string }) {
    try {
      const result = await apolloClient.query({
        query: graphql.EternalBootstrapDocument,
        variables: {
          profile,
        },
      });
      if (result.data) {
        setEternalBootstrap({
          game: result.data.eternalGame,
          categories: result.data.eternalCategories,
          featured: result.data.eternalFeaturedMods,
        });
      }
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  async function loadEternalInstalledMods({ modIds }: { modIds: Array<number> }) {
    try {
      const result = await apolloClient.query({
        query: graphql.EternalModsDocument,
        variables: {
          modIds,
        },
      });
      if (result.data) {
        setEternalInstalledMods({
          mods: result.data.eternalMods,
        });
      }
    } catch (e) {
      handleApolloErrorDefault(e);
    }
  }

  return {
    eternalSearchMods,
    eternalMods,
    clearEternalSearchResult,
    loadEternalBootstrap,
    setEternalInstalledMods,
    loadEternalInstalledMods,
    setEternalBootstrap,
    setEternalSearchResult,
    appendEternalSearchResult,
  };
});
